import jsPDF from 'jspdf'

/**
 * Schema del feedback prodotto da Claude — vedi backend
 * src/services/claudeAI.ts → `generaFeedback`. Lo replico qui invece
 * di riusare il type del componente per disaccoppiare la utility dal
 * resto della UI: la firma resta stabile anche se i componenti React
 * cambiano shape interna.
 */
export interface FeedbackPdf {
  punti_forti: string[]
  aree_miglioramento: string[]
  citazioni: Array<{ chi: string; testo: string; commento: string }>
  consiglio_pratico: string
  scores: {
    apertura: number
    obiezioni: number
    chiusura: number
    complessivo: number
  }
  obiettivo_raggiunto?: boolean
  obiettivo_score?: number
  obiettivo_commento?: string
}

export interface SessionePdf {
  id: string
  /** ISO string */
  createdAt: string
  durataSecondi: number
  obiettivoScelto?: string | null
  utente: { nome: string; cognome: string }
  feedback: FeedbackPdf
}

type RGB = [number, number, number]

/**
 * Genera e fa scaricare il PDF del feedback di una sessione RingCoach.
 *
 * Costruito programmaticamente con jsPDF (niente html2canvas) per
 * massimo controllo: layout deterministico indipendente dal rendering
 * della pagina React, peso < 20KB, supporto caratteri italiani via
 * font Helvetica WinAnsi standard.
 */
export async function esportaFeedbackPDF(sessione: SessionePdf): Promise<void> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const W = doc.internal.pageSize.getWidth()
  const H = doc.internal.pageSize.getHeight()
  const MARGIN = 20
  const CONTENT_W = W - MARGIN * 2
  let y = 0

  // ── Palette brand ────────────────────────────────────────
  const C_BRAND: RGB    = [108, 71, 255]
  const C_DARK: RGB     = [10, 10, 15]
  const C_MUTED: RGB    = [107, 107, 128]
  const C_SUCCESS: RGB  = [34, 197, 94]
  const C_WARNING: RGB  = [245, 158, 11]
  const C_DANGER: RGB   = [239, 68, 68]
  const C_LIGHT: RGB    = [240, 236, 255]
  const C_WHITE: RGB    = [255, 255, 255]
  const C_GRAY_BG: RGB  = [247, 247, 249]
  const C_BORDER: RGB   = [224, 224, 235]
  const C_SUCCESS_SOFT: RGB = [240, 253, 244]
  const C_WARNING_SOFT: RGB = [255, 251, 235]

  // Colore in base allo score (1-5)
  const scoreColor = (s: number): RGB =>
    s >= 4 ? C_SUCCESS : s >= 3 ? C_WARNING : C_DANGER

  // Voto complessivo (1-10) → scala 1-5 per il colore
  const votoColor = (v: number): RGB => scoreColor(v / 2)

  const dataFormattata = new Date(sessione.createdAt).toLocaleDateString('it-IT', {
    day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })

  const durataMin = Math.floor(sessione.durataSecondi / 60)
  const durataSec = sessione.durataSecondi % 60
  const durataStr = `${durataMin}:${durataSec.toString().padStart(2, '0')}`

  const votoLabel = (v: number): string =>
    v >= 8 ? 'Eccellente' : v >= 6 ? 'Buono' : v >= 4 ? 'Sufficiente' : 'Da migliorare'

  // ── Helpers ───────────────────────────────────────────────
  const checkNuovaPagina = (altezzaNecessaria: number): void => {
    if (y + altezzaNecessaria > H - 20) {
      doc.addPage()
      y = 20
    }
  }

  const roundRect = (
    rx: number, ry: number, rw: number, rh: number,
    fillColor: RGB, strokeColor?: RGB, radius = 3,
  ): void => {
    doc.setFillColor(fillColor[0], fillColor[1], fillColor[2])
    if (strokeColor) {
      doc.setDrawColor(strokeColor[0], strokeColor[1], strokeColor[2])
      doc.setLineWidth(0.3)
      doc.roundedRect(rx, ry, rw, rh, radius, radius, 'FD')
    } else {
      doc.roundedRect(rx, ry, rw, rh, radius, radius, 'F')
    }
  }

  const setText = (c: RGB): void => { doc.setTextColor(c[0], c[1], c[2]) }
  const setFill = (c: RGB): void => { doc.setFillColor(c[0], c[1], c[2]) }

  // Voce con bullet circolare (usata per punti di forza / aree miglior.)
  const renderBullet = (
    testi: string[],
    bulletColor: RGB,
    bgColor: RGB,
  ): void => {
    for (const testo of testi) {
      const righe = doc.splitTextToSize(testo, CONTENT_W - 18)
      const altezza = Math.max(11, righe.length * 5 + 5)
      checkNuovaPagina(altezza + 2)
      roundRect(MARGIN, y, CONTENT_W, altezza, bgColor)
      setFill(bulletColor)
      doc.circle(MARGIN + 5, y + 5.5, 1.5, 'F')
      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      setText(C_DARK)
      doc.text(righe, MARGIN + 11, y + 6.5)
      y += altezza + 2
    }
  }

  // ══════════════════════════════════════════════════════════
  // HEADER
  // ══════════════════════════════════════════════════════════
  setFill(C_BRAND)
  doc.rect(0, 0, W, 22, 'F')
  setText(C_WHITE)
  doc.setFontSize(14)
  doc.setFont('helvetica', 'bold')
  doc.text('RingCoach', MARGIN, 14)
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.text('Feedback Sessione', W - MARGIN, 14, { align: 'right' })

  y = 32

  // ══════════════════════════════════════════════════════════
  // INFO SESSIONE
  // ══════════════════════════════════════════════════════════
  setText(C_DARK)
  doc.setFontSize(18)
  doc.setFont('helvetica', 'bold')
  doc.text(`${sessione.utente.nome} ${sessione.utente.cognome}`, MARGIN, y)
  y += 7

  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')
  setText(C_MUTED)
  doc.text(`${dataFormattata}  ·  Durata: ${durataStr}`, MARGIN, y)
  y += 10

  // Obiettivo se presente
  if (sessione.obiettivoScelto) {
    const obietRighe = doc.splitTextToSize(sessione.obiettivoScelto, CONTENT_W - 32)
    const altezza = Math.max(14, obietRighe.length * 5 + 8)
    roundRect(MARGIN, y, CONTENT_W, altezza, C_LIGHT, undefined, 4)
    setText(C_BRAND)
    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    doc.text('OBIETTIVO', MARGIN + 4, y + 7)
    setText(C_DARK)
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.text(obietRighe, MARGIN + 30, y + 7)
    y += altezza + 4
  }

  // ══════════════════════════════════════════════════════════
  // VOTO COMPLESSIVO
  // ══════════════════════════════════════════════════════════
  y += 2
  const voto = sessione.feedback.scores.complessivo
  const votoC = votoColor(voto)

  roundRect(MARGIN, y, CONTENT_W, 22, C_GRAY_BG, C_BORDER, 4)
  doc.setFontSize(26)
  doc.setFont('helvetica', 'bold')
  setText(votoC)
  doc.text(`${voto}/10`, MARGIN + 8, y + 15)

  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  setText(C_DARK)
  doc.text(votoLabel(voto), MARGIN + 36, y + 10)
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  setText(C_MUTED)
  doc.text('Voto complessivo della sessione', MARGIN + 36, y + 17)

  y += 30

  // ══════════════════════════════════════════════════════════
  // SCORE DETTAGLIATI (3 colonne)
  // ══════════════════════════════════════════════════════════
  const BOX_W = (CONTENT_W - 8) / 3
  const scoreItems = [
    { label: 'Apertura', valore: sessione.feedback.scores.apertura },
    { label: 'Obiezioni', valore: sessione.feedback.scores.obiezioni },
    { label: 'Chiusura', valore: sessione.feedback.scores.chiusura },
  ]
  scoreItems.forEach((item, i) => {
    const bx = MARGIN + i * (BOX_W + 4)
    const col = scoreColor(item.valore)
    roundRect(bx, y, BOX_W, 20, C_GRAY_BG, C_BORDER, 4)
    doc.setFontSize(18)
    doc.setFont('helvetica', 'bold')
    setText(col)
    doc.text(`${item.valore}/5`, bx + BOX_W / 2, y + 12, { align: 'center' })
    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    setText(C_MUTED)
    doc.text(item.label, bx + BOX_W / 2, y + 18, { align: 'center' })
  })
  y += 28

  // ══════════════════════════════════════════════════════════
  // PUNTI DI FORZA
  // ══════════════════════════════════════════════════════════
  if (sessione.feedback.punti_forti.length > 0) {
    checkNuovaPagina(20)
    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    setText(C_DARK)
    doc.text('Punti di forza', MARGIN, y)
    y += 5
    renderBullet(sessione.feedback.punti_forti, C_SUCCESS, C_SUCCESS_SOFT)
    y += 2
  }

  // ══════════════════════════════════════════════════════════
  // AREE DI MIGLIORAMENTO
  // ══════════════════════════════════════════════════════════
  if (sessione.feedback.aree_miglioramento.length > 0) {
    checkNuovaPagina(20)
    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    setText(C_DARK)
    doc.text('Aree di miglioramento', MARGIN, y)
    y += 5
    renderBullet(sessione.feedback.aree_miglioramento, C_WARNING, C_WARNING_SOFT)
    y += 2
  }

  // ══════════════════════════════════════════════════════════
  // CITAZIONI EVIDENZIATE
  // (lo spec parlava di "momento critico" singolo: in realtà il
  // feedback Claude restituisce un array di citazioni con
  // {chi, testo, commento}. Le rendiamo come blocchi compatti.)
  // ══════════════════════════════════════════════════════════
  if (sessione.feedback.citazioni.length > 0) {
    checkNuovaPagina(20)
    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    setText(C_DARK)
    doc.text('Citazioni evidenziate', MARGIN, y)
    y += 5

    for (const c of sessione.feedback.citazioni) {
      const fraseRighe = doc.splitTextToSize(`"${c.testo}"`, CONTENT_W - 8)
      const commentoRighe = doc.splitTextToSize(c.commento, CONTENT_W - 8)
      const altezza = 8 + fraseRighe.length * 5 + commentoRighe.length * 4 + 6
      checkNuovaPagina(altezza + 2)
      roundRect(MARGIN, y, CONTENT_W, altezza, C_GRAY_BG, C_BORDER, 3)
      // chi (es. SDR / LEAD)
      doc.setFontSize(7)
      doc.setFont('helvetica', 'bold')
      setText(C_BRAND)
      doc.text(c.chi.toUpperCase(), MARGIN + 4, y + 6)
      // testo citato
      doc.setFontSize(9)
      doc.setFont('helvetica', 'italic')
      setText(C_DARK)
      doc.text(fraseRighe, MARGIN + 4, y + 11)
      // commento
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      setText(C_MUTED)
      doc.text(commentoRighe, MARGIN + 4, y + 11 + fraseRighe.length * 5 + 2)
      y += altezza + 2
    }
    y += 2
  }

  // ══════════════════════════════════════════════════════════
  // CONSIGLIO PRATICO
  // ══════════════════════════════════════════════════════════
  if (sessione.feedback.consiglio_pratico) {
    const consigliRighe = doc.splitTextToSize(sessione.feedback.consiglio_pratico, CONTENT_W - 8)
    const altezza = 10 + consigliRighe.length * 5
    checkNuovaPagina(altezza + 6)
    roundRect(MARGIN, y, CONTENT_W, altezza, C_LIGHT, C_BRAND, 4)
    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    setText(C_BRAND)
    doc.text('CONSIGLIO PRATICO', MARGIN + 4, y + 7)
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    setText(C_DARK)
    doc.text(consigliRighe, MARGIN + 4, y + 14)
    y += altezza + 6
  }

  // ══════════════════════════════════════════════════════════
  // OBIETTIVO (solo se valutato)
  // ══════════════════════════════════════════════════════════
  if (sessione.feedback.obiettivo_score != null) {
    const raggiunto = sessione.feedback.obiettivo_raggiunto === true
    const bgCol: RGB = raggiunto ? C_SUCCESS_SOFT : C_WARNING_SOFT
    const txtCol: RGB = raggiunto ? C_SUCCESS : C_WARNING
    const commentoRighe = sessione.feedback.obiettivo_commento
      ? doc.splitTextToSize(sessione.feedback.obiettivo_commento, CONTENT_W - 8)
      : []
    const altezza = 10 + (commentoRighe.length > 0 ? commentoRighe.length * 5 : 0)
    checkNuovaPagina(altezza + 4)
    roundRect(MARGIN, y, CONTENT_W, altezza, bgCol)
    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    setText(txtCol)
    doc.text(
      `${raggiunto ? 'OBIETTIVO RAGGIUNTO' : 'OBIETTIVO NON RAGGIUNTO'}  ·  Score ${sessione.feedback.obiettivo_score}/5`,
      MARGIN + 4, y + 7,
    )
    if (commentoRighe.length > 0) {
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(9)
      setText(C_DARK)
      doc.text(commentoRighe, MARGIN + 4, y + 13)
    }
    y += altezza + 4
  }

  // ══════════════════════════════════════════════════════════
  // FOOTER SU OGNI PAGINA
  // ══════════════════════════════════════════════════════════
  const totalePagine = doc.getNumberOfPages()
  for (let p = 1; p <= totalePagine; p++) {
    doc.setPage(p)
    setFill([245, 245, 249])
    doc.rect(0, H - 12, W, 12, 'F')
    doc.setFontSize(7)
    doc.setFont('helvetica', 'normal')
    setText(C_MUTED)
    doc.text('RingCoach — Feedback riservato', MARGIN, H - 5)
    doc.text(`Pagina ${p} di ${totalePagine}`, W - MARGIN, H - 5, { align: 'right' })
  }

  // ── Download ──────────────────────────────────────────────
  const slugCognome = (sessione.utente.cognome || 'utente').replace(/\s+/g, '_')
  const isoDate = new Date(sessione.createdAt).toISOString().slice(0, 10)
  doc.save(`RingCoach_Feedback_${slugCognome}_${isoDate}.pdf`)
}
